import utils from '../utils'
import {TSAssertType} from "../tsmagic"
import {rewriteApiUrlStringFromOriginalToNew, API_BASE} from '../links'
import {TypeMap, StringTypeMap, NumberTypeMap} from "./generaltypemaps"
import {
  TSTypes, EOFields, ApiFields, EOApiResponse, EOFieldsOnlyExpand,
  ExpandFieldKeys, EOExpandFieldKeys, EOApiResponseSingleObject,
  Filter, Filters, ApiObjectFields, APICLASSES,
} from "./types"
import {DUMMY_ACCESS_TOKEN} from "../dummydata/api-constants"


utils.assert(API_BASE.startsWith("https://") && API_BASE.endsWith("/"),
    "Base url should start with https:// and end with /")

function capitalize<S extends string>(s: S): Capitalize<S> {
  return s.charAt(0).toUpperCase() + s.slice(1) as Capitalize<S>
}


export const MeFields = {
  userName: StringTypeMap,
  firstName: StringTypeMap,
  fullName: StringTypeMap,
  currentDivision: NumberTypeMap,
}

export const DivisionFields = {
  code: NumberTypeMap,
  hid: StringTypeMap,
  description: StringTypeMap,
}

class EmptyInArrayError extends Error {}


function createFilterURLParamValues<TSType extends TSTypes>(fieldname: string, filter: Filter<TSType> | undefined, formatter: (val: TSType) => string): Readonly<string[]> {
  const eoFieldname = capitalize(fieldname)
  return Object.entries(filter ?? {}).map(([operator, data]) => {
    switch (operator) {
      case "eq":
      case "ne":
      case "gt":
      case "ge":
      case "lt":
      case "le":
        return `${eoFieldname} ${operator} ${formatter(data as TSType)}`
      case "in":
        if ((data as TSType[]).length === 0) {
         throw new EmptyInArrayError()
        }
        return (data as TSType[]).map(
          datum => `${eoFieldname} eq ${formatter(datum)}`).join(" or ")
      case "ni":
        return (data as TSType[]).map(
          datum => `${eoFieldname} ne ${formatter(datum)}`).join(" and ")
      default: return new Error("No match") as never
    }
  })
}

export class ApiObject {
  // this private var is here so that typescipt 'extends ApiObject` means something
  private __is_api_object__?: never

  constructor(_props: EOFields<APICLASSES>) {
    if (new.target === ApiObject) {
      throw new Error("abstract class " + this.__is_api_object__!)
    }
  }

  static get FIELDS(): ApiFields {
    throw new Error("abstract class")
  }

  static get EXPANDFIELDS(): typeof EMPTYOBJECT {
    return EMPTYOBJECT
  }

  static getFieldNames<CLASS extends APICLASSES>(
      this: CLASS): Array<keyof InstanceType<CLASS>> {
    return [...Object.keys(this.FIELDS)] as Array<keyof InstanceType<CLASS>>
  }

  static getEOFieldNames<CLASS extends APICLASSES>(
      this: CLASS): EOFieldNames<CLASS>[] {
    return Object.keys(this.FIELDS).map(capitalize) as EOFieldNames<CLASS>[]
  }

  private static _applySelectToUrl<CLASS extends APICLASSES>(
      this: CLASS, url: URL): void {
    if (url.searchParams.has("$select")) {
      throw new Error("Url should not have $select yet")
    }
    url.searchParams.set("$select", [
      ...this.getEOFieldNames(),
      ...Object.keys(this.EXPANDFIELDS).map(capitalize)].join(","))
  }

  private static _applyFiltersToUrl<CLASS extends APICLASSES>(
    this: CLASS, url: URL, filters: Filters<CLASS>): void {
    if (url.searchParams.has("$filters")) {
      throw new Error("Url should not have $select yet")
    }
    let filterlines = new Array<string>()
    ;(Object.entries(filters) as [string, Filter<any>][])
      .forEach(<TSType extends TSTypes>(
          [fieldname, filter]: [string, Filter<TSType>]) => {
        TSAssertType<keyof typeof this.FIELDS>(fieldname)
        const typeMap = this.FIELDS[fieldname] as unknown as TypeMap<TSType>
        if (!typeMap) {
          console.log(this, fieldname)
        }
        const formatter = typeMap.toEOFilterValue
        createFilterURLParamValues(fieldname, filter, formatter)
          .forEach(filterline => {filterlines.push(filterline)})
    })
    if (filterlines.length) {
      url.searchParams.set("$filter", filterlines.map(f => `(${f})`).join(" and "))
    }
  }

  protected static async *_getAll<CLASS extends APICLASSES>(
    this: CLASS, api: Api, url: URL, filters: Filters<CLASS>
  ): AsyncGenerator<InstanceType<CLASS>[], void, void> {
    this._applySelectToUrl(url)
    try {
      this._applyFiltersToUrl(url, filters)
    } catch (e) {
      if (e instanceof EmptyInArrayError) {
        return // returns without yielding, should be enough, right?
      }
      throw e
    }
    const dataGenerator = api.makeCallAll<CLASS>(
      url, {expand: Object.keys(this.EXPANDFIELDS) as ExpandFieldKeys<CLASS>})
    for await (let data of dataGenerator) {
      yield data.map(datum =>
        new this(datum as any) as InstanceType<CLASS>
      )
    }
  }

  static async *getAll<CLASS extends APICLASSES>(
    this: CLASS, api: Api, currentDivision: number, filter?: Filters<CLASS>
  ): AsyncGenerator<InstanceType<CLASS>[], void, void> {
    const url = api.getURL(this.URL, currentDivision)
    yield* this._getAll(api, url, filter ?? {})
  }

  async getCopyFromDatabase(
    api: Api, division: number
  ): Promise<this> {
    type CLASS = (this extends InstanceType<infer C> ? C : never) & APICLASSES
    const url = api.getURL(this.SINGLE_ITEM_URL, division)
    const results = await utils.asyncGeneratorGetAllGenerated(
      (this.constructor as CLASS)._getAll<CLASS>(api, url, {}))
    return results.flat()[0]!
  }

  async doUpdate<CLASS extends APICLASSES>(
    this: InstanceType<CLASS>,
    api: Api,
    division: number,
    updates: Partial<EOFields<CLASS>>
  ): Promise<void> {
    const url = api.getURL(this.SINGLE_ITEM_URL, division)
    await api.fetchWithAuthorization(url, {
      body: JSON.stringify(updates),
      method: 'PUT',
      headers: {"Content-Type": "application/json"}
    })
  }


  static get URL(): string {
    throw new Error("Abstract property, please override")
  }

  get SINGLE_ITEM_URL(): string {
    throw new Error("Abstract property, please override")
  }
}


type EOFieldNames<CLASS extends APICLASSES> = keyof EOFields<CLASS>

export class Me extends ApiObject implements ApiObjectFields<typeof MeFields> {
  userName: string
  firstName: string
  fullName: string
  currentDivision: number

  constructor(props: EOFields<typeof Me>) {
    super({})
    this.userName = Me.FIELDS.userName.fromEO(props.UserName)
    this.firstName = Me.FIELDS.firstName.fromEO(props.FirstName)
    this.fullName = Me.FIELDS.fullName.fromEO(props.FullName)
    this.currentDivision = Me.FIELDS.currentDivision.fromEO(props.CurrentDivision)
  }

  static async get(api: Api): Promise<Me> {
    const url = new URL(API_BASE + "api/v1/current/Me")
    return (await utils.asyncGeneratorGetAllGenerated(
      this._getAll(api, url, {})))[0][0]
  }

  static get FIELDS(): typeof MeFields {
    return MeFields
  }
}

export class Division extends ApiObject
    implements ApiObjectFields<typeof DivisionFields> {
  code: number
  hid: string
  description: string

  constructor(props: EOFields<typeof Division>) {
    super({})
    this.code = Division.FIELDS.code.fromEO(props.Code)
    this.hid = Division.FIELDS.hid.fromEO(props.Hid)
    this.description = Division.FIELDS.description.fromEO(props.Description)
  }

  static get FIELDS(): typeof DivisionFields {
    return DivisionFields
  }

  static get URL() {
    return "system/Divisions"
  }
}

type XMLTopic = "PurchaseOrders"


export type LoginInfo = {
  accessToken: string
  validTill: Date
}

type ApiCallOptions<CLASS extends APICLASSES> = {
  expand: ExpandFieldKeys<CLASS>
}

export class Api{
  private previousMe: Me | undefined
  private me?: Promise<Me> | undefined
  private divisions?: Promise<ReadonlyArray<Division>>
  private loginInfo?: LoginInfo
  private waitForApiReadyPromise: Promise<void>
  private waitForApiReadyPromiseResolver: (() => void) | undefined
  private waitingCalls: number

  constructor() {
    const api = this
    this.waitForApiReadyPromise = new Promise((resolve, _reject) => {
      api.waitForApiReadyPromiseResolver = resolve
    })
    this.waitingCalls = 0
  }

  getNumberOfCallsWaitingForLogin() {
    return this.waitingCalls
  }

  async getMe(): Promise<Me> {
    if (!this.me) {
      this.me = new Promise<Me>(async (resolve, reject) => {
        const me = await Me.get(this)
        if (this.previousMe && this.previousMe.userName !== me.userName) {
          reject(new Error("Logged in user changed"))
        }
        this.previousMe = me
        resolve(me)
      })
    }
    return this.me
  }

  async getDivisions(): Promise<readonly Division[]> {
    const me = await this.getMe()
    if (!this.divisions) {
      this.divisions = new Promise<Division[]>(async (resolve, _reject) => {
        let divisions = new Array<Division>()
        for await (let divs of Division.getAll(this, me.currentDivision)) {
          divisions = divisions.concat(divs)
        }
        resolve(divisions)
      })
    }
    return this.divisions
  }

  async setLoginInfo(loginInfo: LoginInfo) {
    this.loginInfo = loginInfo
    this.me = undefined
    this.divisions = undefined
    if (this.waitForApiReadyPromiseResolver) {
      this.waitForApiReadyPromiseResolver()
      this.waitForApiReadyPromiseResolver = undefined
    }
    await this.getMe()
    await this.getDivisions()
    window.setTimeout(() => {
      const api = this
      this.waitForApiReadyPromise = new Promise((resolve, _reject) => {
        api.waitForApiReadyPromiseResolver = resolve
      })
    }, this.getSecondsUntilApiTokenExpires()! * 1000)
  }

  getApiTokenValidTill(): number | undefined {
    if (!this.loginInfo) {
      return undefined;
    }
    return this.loginInfo.validTill.valueOf()
  }

  getSecondsUntilApiTokenExpires(): number | undefined {
    /**
     * Returns how long the current API token will stay valid
     * This may be negative
     * Returns undefined if no current API token yet
     */
    if (!this.loginInfo) {
      return undefined;
    }
    const now = new Date()
    return (this.loginInfo.validTill.valueOf() - now.valueOf()) / 1000
  }

  async fetchWithAuthorization(
    url: URL | string, options?: RequestInit): Promise<Response> {
      let newOptions = {...options} ?? {}
      newOptions.headers = {
        ...await this.getAuthorisationHeader(), ...newOptions.headers ?? {}}
      let tries = 0
      const apiself = this
      async function tryRepeat503Fetch(): Promise<Response> {
        const response =
          apiself.isDummyApi()
          ? await apiself.fetchDummyData(url.toString(), newOptions)
          : await fetch(url.toString(), newOptions)
        if (response.status !== 503 || tries > 7) {
          return response
        }
        tries++;
        console.log(`retrying fetching (${tries}) ${url.toString()}`)
        await utils.asyncSleep(1000 * tries * tries);
        return await tryRepeat503Fetch()
      }
      const response = await tryRepeat503Fetch()
      if (!response.ok) {
        const body = await response.text()
        throw new Error(`Problem fetching; response = ${response.status}; `
          + JSON.stringify(body))
      }
      return response
  }

  isDummyApi() {
    return this.loginInfo && this.loginInfo.accessToken === DUMMY_ACCESS_TOKEN
  }

  async fetchDummyData(url: string, options: RequestInit): Promise<Response> {
    const dummyapi = await import("../dummydata/api")
    return await dummyapi.dummyFetch(url, options)
  }

  private async getAuthorisationHeader() {
    this.waitingCalls++
    await this.waitForApiReadyPromise
    this.waitingCalls--
    return {"Authorization": "Bearer " + this.loginInfo!.accessToken}
  }

  private async makeCall<CLASS extends APICLASSES>(
    url: URL, options: ApiCallOptions<CLASS>,
  ): Promise<EOApiResponse<CLASS>> {
    let paramsFromOptions = new Map<string, string>()
    paramsFromOptions.set(
      "$expand", (options.expand ?? []).map(capitalize).join(","))

    for(const key of ["$top", "$expand"]) {
      if (url.searchParams.has(key)) {
        if (url.searchParams.get(key) !== paramsFromOptions.get(key)) {
          throw new Error(`Use the options to provide a ${key}`)
        }
      } else if (paramsFromOptions.has(key)) {
        url.searchParams.set(key, paramsFromOptions.get(key)!)
      }
    }
    url.searchParams.set(
      "$expand", (options.expand ?? []).map(capitalize).join(","))

    const response = await this.fetchWithAuthorization(
      url, {headers: {Accept: "application/json"}})

    // TODO: error handling
    // TODO: rate limit control
    const responseObj = await response.json()
    if ("__metadata" in (responseObj as any).d) {
      // form in case of single object retrieval
      TSAssertType<{d: EOApiResponseSingleObject<CLASS>}>(responseObj)
      return {d: {results: [responseObj.d]}}
    } else {
      // normal form
      TSAssertType<EOApiResponse<CLASS>>(responseObj)
      return responseObj
    }
  }

  private fixExpandResponse<CLASS extends APICLASSES>(
    response: EOApiResponse<CLASS>,
    capitalizedExpand: EOExpandFieldKeys<CLASS>,
  ): EOFields<CLASS>[] {
    return response.d.results.map(responseitem => {
      const newObject =
        utils.removeKeys(responseitem, ["__metadata"])
      capitalizedExpand.forEach(key => {
        ;(newObject as any)[key] = (responseitem[key] as any).results.map(
          (obj: any) => utils.removeKeys(obj, ["__metadata"]))
      })
      return newObject as unknown as EOFields<CLASS>
    })
  }

  async *makeCallAll<CLASS extends APICLASSES>(
    url: URL, options: ApiCallOptions<CLASS>,
  ): AsyncGenerator<EOFields<CLASS>[], void, void> {
    let nextURL: URL | undefined = url
    while (nextURL !== undefined) {
      const response: EOApiResponse<CLASS> =
        await this.makeCall<CLASS>(nextURL, options)
      nextURL = response.d.__next === undefined ? undefined :
        new URL(rewriteApiUrlStringFromOriginalToNew(response.d.__next)!)
      yield(this.fixExpandResponse<CLASS>(
        response,
        (options.expand ?? []).map(capitalize) as (
          Array<keyof EOFieldsOnlyExpand<CLASS>>)
      ))
    }
  }

  getURL(path: string, division: number): URL {
    if (path.startsWith("/")) {
      throw new Error("Path should not start with /")
    }
    return new URL(`${API_BASE}api/v1/${division}/${path}`)
  }

  getXMLDownloadUrl(division: number, topic: XMLTopic): URL {
    const url = new URL(`${API_BASE}docs/XMLDownload.aspx`)
    url.searchParams.set("_Division_", division.toString())
    url.searchParams.set("Topic", topic)
    return url
  }

  getXMLUploadUrl(division: number, topic: XMLTopic): URL {
    const url = new URL(`${API_BASE}docs/XMLUpload.aspx`)
    url.searchParams.set("_Division_", division.toString())
    url.searchParams.set("Topic", topic)
    return url
  }
}
const EMPTYOBJECT: {[key in never]: never} = {}
